import { NG_VALUE_ACCESSOR } from "@angular/forms";
import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ContentChild,
  HostListener,
  ElementRef,
} from "@angular/core";

import { MedInputSizeType, MedOptionType } from "../form/types";
import { MedTemplateDirective } from "../../directives/med-template.directive";
import { ControlValueAccessorComponent } from "../form/ControlValueAccessorComponent";
import { NgClassType } from "../../types";
import { SELECTOR_EMPTY_MESSAGE } from "../../constants/general";
import { DomUtil } from "../../utils/dom.util";
import { KeyNames } from "../../constants/key-names";

@Component({
  selector: "med-search-bar",
  templateUrl: "./med-search-bar.component.html",
  styleUrls: ["./med-search-bar.component.scss"],
  providers: [{
    multi: true,
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MedSearchBarComponent),
  }],
})
export class MedSearchBarComponent<OptionValueType> extends ControlValueAccessorComponent<string> {
  constructor(protected component: ElementRef) {
    super();
  }

  private _options?: MedOptionType<OptionValueType>[];
  private _hasFocus = false;
  overlayVisible = false;
  readonly itemSelector = ".med-search-bar__list-item";

  @Input() label = "";
  @Input() loading = false;
  @Input() disabled = false;
  @Input() placeholder = "";
  @Input() size: MedInputSizeType = "";
  @Input() emptyMessage: string = SELECTOR_EMPTY_MESSAGE;

  @Output() search = new EventEmitter<string>();
  @Output() onSelect = new EventEmitter<OptionValueType | null>();
  @Output() onBlur = new EventEmitter<FocusEvent>();
  @Output() onFocus = new EventEmitter<FocusEvent>();

  @ContentChild(MedTemplateDirective) optionTemplate?: MedTemplateDirective;

  get options(): MedOptionType<OptionValueType>[] | undefined {
    return this._options;
  }

  get hasValue(): boolean {
    return Boolean(this.value);
  }

  get classList(): NgClassType {
    return {
      extend: this.overlayVisible,
      labeled: this.label,
    };
  }

  get inputClassList(): string {
    const extendSearch = this.overlayVisible ? "extend" : "";
    return ["med-search-bar__input", extendSearch].join(" ");
  }

  @Input() set options(value: MedOptionType<OptionValueType>[] | undefined) {
    this._options = value;
    this.showOverlay();
  }

  @HostListener("keydown.tab")
  @HostListener("keydown.shift.tab")
  hidePopupOnTabDown(): void {
    this.hideOverlay();
  }

  @HostListener("keyup.esc", ["$event"])
  hidePopupOnEsc(event: any): void {
    //This if allows to correctly work with dialog.
    // If a dialog has an opened select - a first esc click will close select and prevent close dialog.
    // Then when focused select has a hidden popup - esc can propagate up to the dialog
    if (this.overlayVisible) {
      event.stopImmediatePropagation();
    }

    this.hideOverlay();
  }

  @HostListener("keydown.arrowdown", ["$event"])
  @HostListener("keydown.arrowup", ["$event"])
  focusFirstOptionOnArrowKey(event: any): void {
    event.preventDefault();
    const firstOption = this.component.nativeElement.querySelector(this.itemSelector);
    firstOption?.focus();
  }

  onOptionKeydown(event: any) {
    if (event.key === KeyNames.TAB && event.shiftKey) {
      this.component.nativeElement.focus();
      return;
    }

    if (event.key === KeyNames.ENTER) {
      event.target.click();
      event.preventDefault();
      return;
    }

    if (event.key === KeyNames.ARROW_UP || event.key === KeyNames.ARROW_DOWN) {
      this.focusNextActiveOption(event);
      event.preventDefault();
      event.stopPropagation();
    }
  }

  focusNextActiveOption(event: KeyboardEvent): void {
    if (event.target) {
      const nextFocusableItem: HTMLElement | null = this.findNextActiveOption(event.target, event);
      nextFocusableItem?.focus();
    }
  }

  findNextActiveOption(item: any, event: KeyboardEvent): HTMLElement | null {
    const nextItem = event.key === KeyNames.ARROW_DOWN
      ? item.nextElementSibling
      : item.previousElementSibling;

    if (nextItem) {
      return DomUtil.hasClass(nextItem, "disabled")
        ? this.findNextActiveOption(nextItem, event)
        : nextItem;
    }

    return  null;
  }

  clear(): void {
    this.value = "";
    this.onSelect.emit(null);
    this.overlayVisible = false;
  }

  handleSelect({ label, value }: MedOptionType<OptionValueType>): void {
    this.value = label;
    this.onSelect.emit(value);
    this.overlayVisible = false;
  }

  handleElementFocus(): void {
    this._hasFocus = true;
    this.onFocus.emit();
    this.showOverlay();
  }

  handleElementBlur(event: any): void {
    if (!event) {
      return;
    }

    //Workaround. Add ability to focus nested elements. When one of them loses - hide dropdown
    if (this.isNestedElementFocused(event)) {
      this.listenNestedElementBlur(event);
      return;
    }

    this._blurElement();
  }

  showOverlay(): void {
    if (this.options && this._hasFocus) {
      this.overlayVisible = true;
    }
  }

  hideOverlay(): void {
    this.overlayVisible = false;
  }

  protected isNestedElementFocused(event?: any): boolean {
    const { relatedTarget } = event || {};
    const { nativeElement } = this.component;
    return nativeElement.contains(relatedTarget);
  }

  private _blurElement(): void {
    if (!this._hasFocus) {
      return;
    }

    this._hasFocus = false;
    this.overlayVisible = false;
    this.onBlur.emit();
  }

  private listenNestedElementBlur(event: any): void {
    event.relatedTarget.addEventListener(
      "blur",
      (nestedEvent: any) => this.onNestedElementBlur(nestedEvent),
      { once: true },
    );
  }

  private onNestedElementBlur(event: any) {
    if (!event.relatedTarget) {
      this._blurElement();
      return;
    }

    if (event.relatedTarget.isEqualNode(this.component.nativeElement)) {
      return;
    }

    if (this.isNestedElementFocused(event)) {
      this.listenNestedElementBlur(event);
      return;
    }

    this._blurElement();
  }
}
